import { useQuery } from '@apollo/client/react';
import { useFocusEffect } from 'expo-router';
import { useCallback, useEffect, useMemo } from 'react';
import { StyleSheet, Text, View } from 'react-native';

import { MeetingMemoEmptyIllustration } from '@/components/atoms/illustrations/MeetingMemoEmptyIllustration';
import { TextLink } from '@/components/atoms/link';
import { ErrorState, LoadingState } from '@/components/atoms/state';
import {
  HandoffMeetingActivityCard,
  type HandoffActivityFilter,
} from '@/components/molecules/ai-baton';
import {
  GetActivitiesDocument,
  GetSmsChatDocument,
  type GetActivitiesQuery,
} from '@/generated/usermobile-bff/graphql';
import { formatTimeOnly } from '@/formatters/formatTimeOnly';
import { useTranslation } from '@/i18n';
import { parseContactIdParam } from '@/models/handoffRouteParams';
import { getUnsettledPendingMessages } from '@/models/pendingSmsChat';
import { groupMessagesByDate, sortMessagesBySentAt, type SmsChatMessage } from '@/models/smsChat';
import { usePendingSmsStore } from '@/stores/usePendingSmsStore';
import { colorSemantic, radii, spacing, unisonColors, useAppTheme } from '@/theme';

const PER_PAGE = 50;
// sms-api dispatches the send to a worker, so a just-sent message lands in the chat a
// moment after the mutation resolves. Refetching on focus catches the usual case, and
// polling covers a worker slower than the screen transition.
const SEND_SETTLE_POLL_MS = 3_000;
// The contact bubble tint comes straight from the chat design; the palette has no
// semantic token for it yet.
const CONTACT_BUBBLE_BACKGROUND = '#d0ede6';
const MEETING_ACTIVITY_TYPE = 'meeting';

type ActivityItem = GetActivitiesQuery['activities']['data'][number];
type MeetingActivity = ActivityItem & { readonly meeting: NonNullable<ActivityItem['meeting']> };

interface HandoffActivityHistoryProps {
  readonly contactId: string | null;
  readonly contactName: string | null;
  readonly filter: HandoffActivityFilter;
  readonly onMessagesRendered?: () => void;
}

const hasMeeting = (activity: ActivityItem): activity is MeetingActivity =>
  activity.meeting != null;

export const HandoffActivityHistory = ({
  contactId,
  contactName,
  filter,
  onMessagesRendered,
}: HandoffActivityHistoryProps) => {
  const { t } = useTranslation();
  const { colors } = useAppTheme();

  const {
    data: smsData,
    loading: smsLoading,
    error: smsError,
    refetch: refetchSmsChat,
    startPolling: startSmsPolling,
    stopPolling: stopSmsPolling,
  } = useQuery(GetSmsChatDocument, {
    variables: { input: { contactId: contactId ?? '', page: 1, perPage: PER_PAGE } },
    fetchPolicy: 'cache-and-network',
    skip: contactId == null || filter !== 'sms',
  });

  // Coming back from the compose screen re-focuses this screen, and the message sent
  // there will not be in the cached chat yet.
  useFocusEffect(
    useCallback(() => {
      if (contactId == null || filter !== 'sms') {
        return;
      }

      void refetchSmsChat();
    }, [contactId, filter, refetchSmsChat]),
  );

  const numericContactId = parseContactIdParam(contactId);

  const {
    data: activitiesData,
    loading: activitiesLoading,
    error: activitiesError,
    refetch: refetchActivities,
  } = useQuery(GetActivitiesDocument, {
    variables: {
      input: {
        contactId: numericContactId ?? 0,
        type: MEETING_ACTIVITY_TYPE,
        page: 1,
        perPage: PER_PAGE,
      },
    },
    fetchPolicy: 'cache-and-network',
    skip: numericContactId == null || filter !== 'correspondence',
  });

  const messageGroups = useMemo(
    () => groupMessagesByDate(sortMessagesBySentAt(smsData?.smsChat.data ?? [])),
    [smsData?.smsChat.data],
  );

  const pending = usePendingSmsStore((state) => state.pending);
  const setPendingSms = usePendingSmsStore((state) => state.setPendingSms);

  const unsettled = useMemo(
    () =>
      getUnsettledPendingMessages({
        pending,
        messages: smsData?.smsChat.data ?? [],
        now: Date.now(),
      }),
    [pending, smsData?.smsChat.data],
  );

  const pendingForContact = useMemo(
    () => unsettled.filter((entry) => entry.contactId === contactId),
    [contactId, unsettled],
  );

  // Once the server row for a sent message arrives it replaces the in-memory copy.
  useEffect(() => {
    if (unsettled.length === pending.length) {
      return;
    }

    setPendingSms(unsettled);
  }, [pending.length, setPendingSms, unsettled]);

  // Poll only while a sent message is still waiting for its row. Each poll re-renders,
  // which is also what lets an unsettled message reach its timeout and disappear.
  useEffect(() => {
    if (pendingForContact.length === 0) {
      return;
    }

    startSmsPolling(SEND_SETTLE_POLL_MS);

    return stopSmsPolling;
  }, [pendingForContact.length, startSmsPolling, stopSmsPolling]);

  // The newest message sits at the bottom, so the chat has to open there. A just-sent
  // message is appended below everything else, so its bubble has to bring the view down
  // with it too.
  useEffect(() => {
    if (filter !== 'sms') {
      return;
    }

    if (messageGroups.length === 0 && pendingForContact.length === 0) {
      return;
    }

    onMessagesRendered?.();
  }, [filter, messageGroups, onMessagesRendered, pendingForContact.length]);

  const meetingActivities = useMemo(
    () => (activitiesData?.activities.data ?? []).filter(hasMeeting),
    [activitiesData?.activities.data],
  );

  const handleRetrySmsChat = useCallback(() => {
    void refetchSmsChat();
  }, [refetchSmsChat]);

  const handleRetryActivities = useCallback(() => {
    void refetchActivities();
  }, [refetchActivities]);

  const renderError = (message: string, onRetry: () => void) => (
    <View style={styles.errorContainer} testID="handoff-activity-error">
      <ErrorState message={message} />
      <TextLink
        label={t('aiTasks.detail.retry')}
        testID="handoff-activity-retry"
        onPress={onRetry}
      />
    </View>
  );

  const renderEmpty = (label: string) => (
    <View style={styles.emptyContainer} testID="handoff-activity-empty">
      <MeetingMemoEmptyIllustration />
      <Text style={[styles.emptyText, { color: colors.textNdSecondary }]}>{label}</Text>
    </View>
  );

  const renderMessage = (message: SmsChatMessage) => {
    const isOutbound = message.direction === 'OUTBOUND';
    const senderName = isOutbound
      ? (message.sender.name ?? t('aiTasks.detail.aiAgentName'))
      : (message.sender.name ?? contactName ?? t('aiTasks.detail.customerName'));

    return (
      <View
        key={message.id}
        style={[styles.bubble, isOutbound ? styles.bubbleOutbound : styles.bubbleInbound]}
        testID={`handoff-sms-message-${message.id}`}>
        <View style={styles.bubbleHeader}>
          <Text style={[styles.bubbleSender, { color: colors.textNdPrimary }]}>{senderName}</Text>
          <Text style={[styles.bubbleTime, { color: colors.textNdSecondary }]}>
            {formatTimeOnly(message.sentAt)}
          </Text>
        </View>
        <Text style={[styles.bubbleBody, { color: colors.textNdPrimary }]}>{message.body}</Text>
      </View>
    );
  };

  const renderSmsChat = () => {
    if (smsLoading && !smsData) {
      return <LoadingState />;
    }

    if (smsError != null && smsData == null) {
      return renderError(t('aiTasks.detail.smsLoadError'), handleRetrySmsChat);
    }

    if (messageGroups.length === 0 && pendingForContact.length === 0) {
      return renderEmpty(t('aiTasks.detail.smsEmpty'));
    }

    return messageGroups.map((group) => (
      <View key={group.date} style={styles.dateGroup}>
        <View style={styles.dateSeparator}>
          <View style={styles.dateLine} />
          <Text style={[styles.dateHeader, { color: colors.textNdSecondary }]}>{group.date}</Text>
          <View style={styles.dateLine} />
        </View>
        {group.messages.map(renderMessage)}
      </View>
    ));
  };

  const renderMeetings = () => {
    if (activitiesLoading && !activitiesData) {
      return <LoadingState />;
    }

    if (activitiesError != null && activitiesData == null) {
      return renderError(t('aiTasks.detail.meetingsLoadError'), handleRetryActivities);
    }

    if (meetingActivities.length === 0) {
      return renderEmpty(t('aiTasks.detail.correspondenceEmpty'));
    }

    return (
      <View style={styles.meetingList}>
        {meetingActivities.map((activity) => (
          <HandoffMeetingActivityCard key={String(activity.id)} activity={activity} />
        ))}
      </View>
    );
  };

  if (contactId == null) {
    return renderEmpty(t('aiTasks.detail.noContact'));
  }

  return (
    <View style={styles.container} testID="handoff-activity-history">
      {filter === 'sms' ? renderSmsChat() : renderMeetings()}
      {filter === 'sms'
        ? pendingForContact.map((entry) => (
            <View
              key={entry.messageId}
              style={[styles.bubble, styles.bubbleOutbound, styles.bubblePending]}
              testID={`handoff-sms-pending-${entry.messageId}`}>
              <View style={styles.bubbleHeader}>
                <Text style={[styles.bubbleSender, { color: colors.textNdPrimary }]}>
                  {t('aiTasks.detail.aiAgentName')}
                </Text>
                <Text style={[styles.bubbleTime, { color: colors.textNdSecondary }]}>
                  {t('aiTasks.detail.smsSending')}
                </Text>
              </View>
              <Text style={[styles.bubbleBody, { color: colors.textNdPrimary }]}>{entry.body}</Text>
            </View>
          ))
        : null}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    rowGap: spacing[3],
    paddingBottom: spacing[4],
  },
  dateGroup: {
    rowGap: spacing[3],
  },
  dateSeparator: {
    alignItems: 'center',
    columnGap: spacing[3],
    flexDirection: 'row',
  },
  dateLine: {
    backgroundColor: unisonColors.borderNdBase,
    flex: 1,
    height: 1,
  },
  dateHeader: {
    fontSize: 12,
    lineHeight: 20,
    textAlign: 'center',
  },
  meetingList: {
    rowGap: spacing[2],
  },
  bubble: {
    borderRadius: radii.md,
    maxWidth: '85%',
    paddingHorizontal: spacing[4],
    paddingVertical: spacing[2],
    rowGap: spacing[1],
  },
  bubblePending: {
    opacity: 0.6,
  },
  bubbleOutbound: {
    alignSelf: 'flex-end',
    backgroundColor: colorSemantic.colorBgNlStronger,
  },
  bubbleInbound: {
    alignSelf: 'flex-start',
    backgroundColor: CONTACT_BUBBLE_BACKGROUND,
  },
  bubbleHeader: {
    columnGap: spacing[3],
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  bubbleSender: {
    fontSize: 12,
    fontWeight: '700',
    lineHeight: 16,
  },
  bubbleTime: {
    fontSize: 11,
    lineHeight: 16,
  },
  bubbleBody: {
    fontSize: 14,
    lineHeight: 20,
  },
  errorContainer: {
    alignItems: 'center',
    flex: 1,
    justifyContent: 'center',
  },
  emptyContainer: {
    alignItems: 'center',
    flex: 1,
    justifyContent: 'center',
    rowGap: spacing[3],
  },
  emptyText: {
    fontSize: 14,
    lineHeight: 20,
    textAlign: 'center',
  },
});
